/**
 * Session plumbing: reading a token's expiry, and where to send someone whose session ended.
 *
 * The API hands out a JWT that lives for a fixed number of hours with no sliding window
 * inside it. Three things have to happen for that not to look like the app is broken:
 *
 *   1. A token that is ALREADY expired at page load is dropped before any request goes
 *      out, so the user goes straight to the sign-in page instead of watching every panel
 *      fail one by one.
 *   2. A token that expires mid-session ends the session once, with a clear message —
 *      not a "Failed to load…" toast on every screen the user opens afterwards.
 *   3. A token that is close to expiry while the user is still working gets renewed.
 */

/** Seconds of slack: treat a token as dead slightly early rather than send a doomed request. */
const CLOCK_SKEW_SECONDS = 30;

/** Renew once the token has less than this long to live. */
export const REFRESH_WHEN_WITHIN_MS = 6 * 60 * 60 * 1000;

/** The payload of a JWT, or null if it is not one. No verification — the server does that. */
export function decodeToken(token) {
  if (typeof token !== 'string' || token.split('.').length !== 3) return null;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '='))
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const data = JSON.parse(json);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/** When the token expires, in ms since the epoch — null when it carries no expiry. */
export function tokenExpiryMs(token) {
  const exp = decodeToken(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

/** How long the token has left, in ms. Infinity when it carries no expiry. */
export function tokenTimeLeftMs(token, now = Date.now()) {
  const expiry = tokenExpiryMs(token);
  if (expiry === null) return Infinity;
  return expiry - CLOCK_SKEW_SECONDS * 1000 - now;
}

export function isTokenExpired(token, now = Date.now()) {
  if (!token) return true;
  return tokenTimeLeftMs(token, now) <= 0;
}

export function shouldRefreshToken(token, now = Date.now()) {
  const left = tokenTimeLeftMs(token, now);
  return Number.isFinite(left) && left > 0 && left < REFRESH_WHEN_WITHIN_MS;
}

/** True when a failed request failed because the session is no longer good. */
export function isAuthFailure(error) {
  return error?.response?.status === 401;
}

/*
 * Which portal the person signs in through.
 *
 * Kept under its own key because clearing a session wipes everything else — and the one
 * thing we must NOT forget when a session dies is which door the user came in by.
 * Without it an expired learner lands on the platform sign-in page, which does not accept
 * their credentials at all.
 */
const PORTAL_KEY = 'lms_last_portal';

export function rememberPortal(subdomain) {
  try {
    if (subdomain) localStorage.setItem(PORTAL_KEY, String(subdomain));
  } catch { /* private mode */ }
}

export function rememberedPortal() {
  try {
    return localStorage.getItem(PORTAL_KEY) || localStorage.getItem('lms_tenant_subdomain') || null;
  } catch {
    return null;
  }
}

export function forgetPortal() {
  try { localStorage.removeItem(PORTAL_KEY); } catch { /* private mode */ }
}

/**
 * Where to send someone after their session ends: back to the portal they came in through,
 * so a tenant's learner is not dumped on the platform sign-in page.
 */
export function loginPathForSession({ role, subdomain } = {}) {
  if (role === 'super_admin') return '/login';
  const portal = subdomain || rememberedPortal();
  return portal ? `/tenant/${portal}/login` : '/login';
}
